import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import type { Empty } from "../../types/wasp";
import { useAction, useQuery } from "wasp/client/operations";
import {
  getCurrentMealPlan,
  getUserMealPlans,
  getUsageStatus,
  generateMealPlan,
  regenerateMealPlan,
  getAuthIdentity,
  getDemoPlan,
  downloadPlanPdf,
  refreshDemoPlan,
  getPlanMetrics,
  emailWeeklyMealPlan,
  resendEmailVerification,
  getUserProfile,
  ensureTasteProfile,
  getProfileInsightsEnvelope,
  // swapRecipe, // Removed for now
} from "wasp/client/operations";
import { toast } from "sonner";
import {
  ProgressiveSkeleton,
  PROGRESSIVE_SKELETONS,
} from "../components/ui/progressive-skeleton";
import { AnimatedSkeleton } from "../components/ui/animated-skeleton";
import { trackEvent } from "../config/analytics";
import { Events } from "../../client/analytics/events";
import { MealPlanErrorBoundary } from "../../components/MealPlanErrorBoundary";
import { AiErrorBoundary } from "../../components/AiErrorBoundary";
import { computeUsage, type Tier } from "../../lib/planLimits";
import { normalizeTier } from "../../billing/tiers";
import { isDevBetaEnabled, isDemoModeEnabled } from "../../utils/devMode";
import { useCallback, useMemo } from "react";
import { EasterEggMatrix } from "../components/EasterEggMatrix";
import { useTripleSpaceToggle } from "../hooks/useTripleSpaceToggle";
import { useShakeToggle } from "../hooks/useShakeToggle";
import { PostOnboardingCelebration } from "../components/onboarding/PostOnboardingCelebration";
import { TasteProfileInsightsWidget } from "../components/onboarding/TasteProfileInsightsWidget";
import {
  DashboardGrid,
  DashboardCard,
} from "../components/Dashboard/DashboardLayout";
import { HeroSection } from "../components/Dashboard/HeroSection";
import { TodaysMeals } from "../components/Dashboard/TodaysMeals";
import { WeekCalendar } from "../components/Dashboard/WeekCalendar";
import { QuickStats } from "../components/Dashboard/QuickStats";
import { RecipeRecommendations } from "../components/Dashboard/RecipeRecommendations";
import { usePersonalizedRecipesV2 } from "../../hooks/usePersonalizedRecipesV2";
import { Button } from "../components/ui/button";

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const isAuthenticated = !!user;
  const {
    data: currentMealPlan,
    isLoading: planLoading,
    refetch: refetchCurrent,
  } = useQuery(getCurrentMealPlan, {}, { enabled: isAuthenticated });
  const { data: mealPlanHistory, refetch: refetchHistory } = useQuery(
    getUserMealPlans,
    {},
    { enabled: isAuthenticated },
  );
  const { data: usageStatus } = useQuery(
    getUsageStatus,
    {},
    { enabled: isAuthenticated },
  );
  const { data: authIdentity } = useQuery(
    getAuthIdentity,
    {},
    { enabled: isAuthenticated },
  );
  const { data: demoPlan } = useQuery(
    getDemoPlan,
    {},
    { enabled: isAuthenticated && isDemoModeEnabled() },
  );

  // Kick off taste profile ensure/build once authenticated
  const doEnsureTasteProfile = useAction(ensureTasteProfile as any);
  React.useEffect(() => {
    if (isAuthenticated) {
      // fire-and-forget; server dedupes builds
      doEnsureTasteProfile({}).catch(() => {});
    }
  }, [isAuthenticated]);

  // Deterministic insights envelope with polling while pending
  const { data: insightsEnvelope } = useQuery(
    getProfileInsightsEnvelope,
    {},
    {
      enabled: isAuthenticated,
      // Performance optimizations to prevent excessive refetching
      staleTime: 5 * 60 * 1000,       // Consider data fresh for 5 minutes
      gcTime: 10 * 60 * 1000,         // Keep unused data in cache for 10 minutes
      refetchOnWindowFocus: false,    // Don't refetch when window regains focus
      refetchOnMount: false,          // Don't refetch if data is already cached
      refetchOnReconnect: false,      // Don't refetch on network reconnect
      // Keep existing polling logic (only when pending/failed)
      refetchInterval: (data) =>
        data?.status === 'pending' || data?.status === 'failed' ? 5000 : false,
    },
  );

  // Stable skeletons to avoid recreating arrays on each render
  const dashSkeletons = useMemo(() => PROGRESSIVE_SKELETONS.dashboard(), []);
  const { data: planMetrics } = useQuery(
    getPlanMetrics,
    (currentMealPlan as any)?.id
      ? { planId: (currentMealPlan as any).id }
      : undefined,
    { enabled: isAuthenticated && !!(currentMealPlan as any)?.id },
  );
  const doGeneratePlan = useAction(generateMealPlan);
  const doRegeneratePlan = useAction(regenerateMealPlan);
  const doDownloadPdf = useAction(downloadPlanPdf);
  const doRefreshDemoPlan = useAction(refreshDemoPlan);
  const doResendEmailVerification = useAction(resendEmailVerification);
  const doEmailPlan = useAction(emailWeeklyMealPlan as any);

  // V2 personalized recipes (basic insight)
  const { recipes: v2Recs = [], isLoading: isLoadingV2 } =
    usePersonalizedRecipesV2({ limit: 6, insightMode: "basic" });
  const dashRecs = (v2Recs as any[]).map((it) => {
    const r = it?.recipe ?? {};
    return {
      id: r.id,
      title: r.title,
      image: r.imageUrl,
      cookTime: r.totalMinutes ?? r.prepTime ?? undefined,
      servings: r.servings ?? undefined,
    };
  });

  // Profile completeness (basic heuristic) and tier-gated features
  const { data: userProfile } = useQuery(
    getUserProfile,
    {},
    { enabled: isAuthenticated },
  );
  const hasDietary =
    Array.isArray((userProfile as any)?.dietaryRestrictions) &&
    (userProfile as any)?.dietaryRestrictions.length > 0;
  const hasTastePrefs = !!(userProfile as any)?.tastePreferences;
  const needsProfile = !(hasDietary && hasTastePrefs);
  //   //   //   //   // const doSwapRecipe = useAction(swapRecipe as any); // Removed for now // Removed for now // Removed for now // Removed for now // Removed for now
  const [pending, setPending] = useState<
    "none" | "generate" | "regenerate" | "download" | "refresh"
  >("none");
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(false);
  const [showEgg, setShowEgg] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const [justCompletedOnboarding, setJustCompletedOnboarding] = useState(false);

  const hasInsights = Boolean(
    insightsEnvelope && (insightsEnvelope as any).status === 'ready' && (insightsEnvelope as any).insights,
  );
  React.useEffect(() => {
    if (!hasInsights) return;
    const key = `temptaste_celebration_shown_${user?.id ?? 'anon'}`;
    if (!localStorage.getItem(key)) {
      setShowCelebration(true);
      localStorage.setItem(key, '1');
    }
  }, [hasInsights, user?.id]);

  // Check if user just completed onboarding
  useEffect(() => {
    const flag = localStorage.getItem("onboardingJustCompleted");
    setJustCompletedOnboarding(flag === "1");
  }, []);

  // Hydrate from sessionStorage
  useEffect(() => {
    const v = sessionStorage.getItem("matrix-easter");
    if (v === "1") setShowEgg(true);
  }, []);
  // Persist on change
  useEffect(() => {
    sessionStorage.setItem("matrix-easter", showEgg ? "1" : "0");
  }, [showEgg]);

  const toggle = useCallback(() => setShowEgg((v) => !v), []);
  useTripleSpaceToggle(toggle);

  const { enabled, requestPermission } = useShakeToggle({
    onToggle: toggle,
    threshold: 16,
    cooldownMs: 1200,
  });

  const isMobile =
    typeof navigator !== "undefined" &&
    /Mobi|Android/i.test(navigator.userAgent);

  // Map Wasp's current meal plan into the v3.2 dashboard shape
  function primaryFromDay(d: any) {
    if (!d) return { recipe: undefined } as any;
    if (d.recipe) return { recipe: d.recipe };
    const meals = d.meals || {};
    if (meals.dinner) return { recipe: meals.dinner };
    const firstKey = Object.keys(meals)[0];
    if (firstKey) return { recipe: meals[firstKey] };
    return { recipe: undefined };
  }
  function toV32Plan(plan: any) {
    if (!plan?.planData) return undefined;
    const days = Array.isArray(plan.planData.days) ? plan.planData.days : [];
    return {
      id: plan.id,
      weekRange: new Date(plan.weekStartDate).toLocaleDateString(),
      meals: days.map((d: any) => {
        const { recipe } = primaryFromDay(d);
        const title = recipe?.title || recipe?.name || "Recipe";
        const image =
          recipe?.image ||
          recipe?.imageUrl ||
          recipe?.heroImageUrl ||
          "/api/placeholder/400/300";
        const prep = recipe?.prepTime || recipe?.readyInMinutes || 20;
        const cals = recipe?.nutritionInfo?.calories || recipe?.calories || 400;
        const dayLabel = d?.dateISO
          ? new Date(d.dateISO).toLocaleDateString(undefined, {
              weekday: "long",
            })
          : "Day";
        return {
          day: dayLabel,
          title,
          image,
          prepTime: prep,
          calories: cals,
          canSwap: true,
        };
      }),
    };
  }

  const currentV32 = currentMealPlan ? toV32Plan(currentMealPlan) : undefined;

  // Determine if email verification banner should be shown
  const shouldShowEmailBanner =
    !emailBannerDismissed &&
    authIdentity?.method === "email" &&
    user &&
    !user.emailVerified;

  // Determine user tier with dev mode bypass
  const normalized = normalizeTier((user as any)?.subscriptionTier);
  const userTier: Tier = isDevBetaEnabled()
    ? "core_plus"
    : (user as any)?.subscriptionTier
      ? normalized
      : "free_trial";

  // Calculate usage with proper limits
  const planUsage = computeUsage(userTier, usageStatus?.plansUsed ?? 0);
  const swapUsage = computeUsage(userTier, usageStatus?.swapsUsed ?? 0);

  // Provide sensible defaults while loading (map to v3.2 component props)
  const dashboardProps = {
    userName:
      (user as any)?.firstName ||
      (user as any)?.displayName ||
      user?.email?.split("@")[0] ||
      "Chef",
    currentTier: (userTier === "free_trial"
      ? "demo"
      : normalizeTier(userTier)) as any,
    usageStats: {
      plansUsed: planUsage.used,
      plansLimit: planUsage.limit,
      swapsUsed: swapUsage.used,
      swapsLimit: swapUsage.limit,
      daysUntilReset: 7,
    },
    mealPlanState: (planLoading || pending !== "none"
      ? "generating"
      : currentV32
        ? "ready"
        : "empty") as "generating" | "ready" | "empty",
    currentMealPlan: currentV32,
    showEmailBanner: !!shouldShowEmailBanner,
    onCreatePlan: async () => {
      if (pending === "generate") return;
      trackEvent("generate_plan_click");
      try {
        setPending("generate");
        await doGeneratePlan({} satisfies Empty);
        await refetchCurrent();
        await refetchHistory();
      } catch (e) {
        console.error("Failed to generate plan", e);
      } finally {
        setPending("none");
      }
    },
    onViewPlan: async (planId?: string) => {
      try {
        if (planId) {
          navigate(`/plans/${planId}`);
        } else {
          navigate("/plans");
        }
      } catch (error) {
        console.error("Error navigating to plan:", error);
      }
    },
    onDiscoverRecipes: () => navigate("/discover"),
    onUpgrade: () => console.log("Upgrade clicked"),
    onDismissEmailBanner: () => setEmailBannerDismissed(true),
    onResendEmail: async () => {
      try {
        await doResendEmailVerification({} satisfies Empty);
        toast.success("Verification email sent! Check your inbox.");
        trackEvent(Events.ResendVerificationSuccess);
      } catch (error) {
        console.error("Failed to resend email verification:", error);
        const errorCode = (error as any)?.code || "unknown_error";
        toast.error("Failed to send verification email. Please try again.");
        trackEvent(Events.ResendVerificationFail, { errorCode });
      }
    },
    onSwapRecipe: async (mealId: string) => {
      try {
        trackEvent("recipe_swap_click" as any, { mealId });
        const planId = (currentMealPlan as any)?.id;
        // Swap functionality removed for now
        // if (!planId) {
        //   toast.error("No meal plan available to swap from");
        //   return;
        // }
        // await doSwapRecipe({
        //   mealPlanId: planId,
        //   oldRecipeId: mealId,
        //   mealType: "dinner",
        //   dayIndex: 0,
        // });
        toast.success("Recipe swapped successfully!");
        refetchCurrent();
      } catch (error) {
        console.error("Error swapping recipe:", error);
        toast.error("Failed to swap recipe. Please try again.");
      }
    },
    onDownloadPlan: async () => {
      const planId = (currentMealPlan as any)?.id;
      if (pending === "download" || !planId) return;
      try {
        setPending("download");
        const result: any = await doDownloadPdf({ id: planId });
        if (result?.url) window.open(result.url, "_blank");
        trackEvent("download_plan_pdf" as any, { planId });
      } catch (error) {
        console.error("Error downloading plan:", error);
      } finally {
        setPending("none");
      }
    },
    onEmailPlan: async () => {
      const planId = (currentMealPlan as any)?.id;
      try {
        await doEmailPlan(planId ? { mealPlanId: planId } : {});
        toast.success("Plan emailed");
      } catch (e) {
        console.error("Email plan failed", e);
        toast.error("Could not email plan");
      }
    },
    onRegenerate: async () => {
      trackEvent("regenerate_plan_click");
      const planId = (currentMealPlan as any)?.id;
      if (!planId) return;
      try {
        setPending("regenerate");
        await doRegeneratePlan({ mealPlanId: planId } as any);
        await refetchCurrent();
        await refetchHistory();
      } catch (e) {
        console.error("Failed to regenerate plan", e);
      } finally {
        setPending("none");
      }
    },
    onRefreshPlan: async () => {
      if (pending === "refresh") return;
      if (!isDemoModeEnabled()) return;
      try {
        setPending("refresh");
        await doRefreshDemoPlan(undefined);
        trackEvent("refresh_demo_plan" as any);
      } catch (error) {
        console.error("Error refreshing demo plan:", error);
      } finally {
        setPending("none");
      }
    },
    onShoppingList: () => {
      try {
        trackEvent("button_click" as any, { button: "shopping_list" });
        navigate("/shopping-list");
      } catch (error) {
        console.error("Error opening shopping list:", error);
      }
    },
    onNutrition: () => {
      try {
        trackEvent("button_click" as any, { button: "nutrition" });
        navigate("/nutrition");
      } catch (error) {
        console.error("Error opening nutrition:", error);
      }
    },
    onRecipeInfo: (recipe: any) => {
      try {
        trackEvent("button_click" as any, {
          button: "recipe_info",
          recipeId: recipe.id,
        });
        navigate(`/recipe/${recipe.id}`);
      } catch (error) {
        console.error("Error opening recipe info:", error);
      }
    },
    planMetrics: planMetrics || null,
    buttonStates: {
      downloading: pending === "download",
      refreshing: pending === "refresh",
      regenerating: pending === "regenerate",
    },
  };

  const isLoadingAny = planLoading || !usageStatus;

  useEffect(() => {
    trackEvent("dashboard_view");
  }, []);

  return (
      <MealPlanErrorBoundary>
        <AiErrorBoundary>
          {/* Phase 2: Post-Onboarding Celebration Modal */}
          {showCelebration && hasInsights ? (
            <PostOnboardingCelebration
              isOpen={showCelebration}
              onOpenChange={(open) => {
                setShowCelebration(open);
                if (!open && user?.id) {
                  localStorage.setItem(
                    `temptaste_celebration_shown_${user.id}`,
                    "true",
                  );
                }
              }}
              onDiscoverClick={() => navigate("/discover")}
              onDetailedProfileClick={() => navigate("/onboarding")}
              autoCloseDuration={8000}
              insightsData={(insightsEnvelope as any)?.insights ?? null}
            />
          ) : null}

          {isMobile && !enabled && (
            <div className="p-6">
              <button
                className="mt-3 rounded border px-2 py-1 text-xs hover:bg-accent"
                onClick={requestPermission}
              >
                Enable "shake to toggle"
              </button>
            </div>
          )}
          {showEgg && (
            <div className="p-6">
              <EasterEggMatrix onClose={() => setShowEgg(false)} />
            </div>
          )}
          {isLoadingAny ? (
            <div className="space-y-6">
              <AnimatedSkeleton
                variant="shimmer"
                className="h-8 w-72 rounded-md"
              />
              <ProgressiveSkeleton skeletons={dashSkeletons} duration={1800} />
            </div>
          ) : (
            <>
              {/* Phase 2: Taste Profile Insights Widget */}
              <div className="py-4">
                <TasteProfileInsightsWidget
                  showEditButton={true}
                  onEditClick={() => navigate("/onboarding")}
                />
              </div>

              {/* Post-onboarding recipe selection is handled within the onboarding flow (TastePairs). */}

              {/* Main Dashboard */}
              <div className="space-y-6 py-6">
                <DashboardGrid>
                  {/* Profile CTA (basic) */}
                  {needsProfile && (
                    <DashboardCard>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-dusk">
                            Complete your profile
                          </h3>
                          <p className="text-supporting text-sm">
                            Add dietary restrictions and taste preferences for
                            better matches.
                          </p>
                        </div>
                        <button
                          onClick={() => navigate("/onboarding")}
                          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
                        >
                          Complete Profile
                        </button>
                      </div>
                    </DashboardCard>
                  )}

                  {/* Hero Section - Full Width */}
                  <HeroSection userName={dashboardProps.userName} />

                  {/* Today's Meals - 4 cols */}
                  <TodaysMeals
                    meals={{
                      breakfast: undefined,
                      lunch: undefined,
                      dinner: undefined,
                    }}
                    onMealClick={(mealId) => navigate(`/recipe/${mealId}`)}
                  />

                  {/* Week Calendar - 4 cols */}
                  <WeekCalendar onViewFullPlan={dashboardProps.onViewPlan} />

                  {/* Quick Stats - 4 cols */}
                  <QuickStats
                    plansCreated={
                      Array.isArray(mealPlanHistory)
                        ? mealPlanHistory.length
                        : 0
                    }
                    recipesSaved={0}
                    shoppingLists={0}
                    profileCompletion={75}
                    onStatClick={(stat) => {
                      if (stat === "plans") navigate("/plans");
                      if (stat === "recipes") navigate("/favorites");
                      if (stat === "shopping") navigate("/coming-soon");
                      if (stat === "profile") navigate("/onboarding");
                    }}
                  />

                  {/* Recipe Recommendations - Full Width */}
                  {!justCompletedOnboarding ? (
                    <RecipeRecommendations
                      recipes={dashRecs}
                      onRecipeClick={(recipeId) => {
                        if (recipeId === "discover") navigate("/discover");
                        else navigate(`/recipe/${recipeId}`);
                      }}
                      onSaveRecipe={(recipeId) => {
                        toast.success("Recipe saved!");
                      }}
                    />
                  ) : (
                    <DashboardCard className="!col-span-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-dusk">
                            Continue Exploring
                          </h3>
                          <p className="text-supporting text-sm">
                            Discover more recipes tailored to your preferences.
                          </p>
                        </div>
                        <Button
                          onClick={() => navigate("/discover")}
                          className="bg-coral text-white hover:bg-coral/90"
                        >
                          Explore →
                        </Button>
                      </div>
                    </DashboardCard>
                  )}
                </DashboardGrid>
              </div>
            </>
          )}
        </AiErrorBoundary>
      </MealPlanErrorBoundary>
  );
}
